import { useMemo, useState } from 'react';

import { PopoverTrigger } from '@radix-ui/react-popover';
import {
  ColumnFiltersState,
  createColumnHelper,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
  filterFns,
} from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';

import { InconsistenciesLoader } from './components/inconsistencies-loader';
import { Inconsistency } from '../../../../../../../../../backend/src/case/inconsistency/inconsistency.entity';
import { Relevance } from '../../../../../../../../../backend/src/types/types';
import { CountBadge } from '../../../../../../components/count-badge';
import { SubsetChip } from '../../../../../../components/subset-chip';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateInconsistencyReport } from '@/api/mutations/useCreateInconsistencyReport';
import { useGetAllSubsetInconsistencies } from '@/api/queries/useGetAllSubsetInconsistencies';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { FilterPopover, InconsistencyGlobalFilter } from '@/new/components/inconsistency/filter-popover';
import { InconsistencyCard } from '@/new/components/inconsistency/inconsistency-card';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover } from '@/new/components/shadcn/popover';
import { TablePagination } from '@/new/components/table/table-pagination';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { includedInArr } from '@/new/lib/table-utils';
import { MyOptionType } from '@/types';
import { buildClientSideFilterPills, FilterPill } from '@/utils/buildFilterPills';

const fallbackTableData: Inconsistency[] = [];
const columnHelper = createColumnHelper<Inconsistency>();

const columns = [
  columnHelper.accessor('level', { filterFn: includedInArr }),
  columnHelper.accessor('description', {}),
  columnHelper.accessor('type', { filterFn: includedInArr }),
  columnHelper.accessor('source', { filterFn: includedInArr }),
];

export function Inconsistencies() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const navigate = useNavigate();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetCase(subsetId);
  const { data: inconsistencies, isLoading: isLoadingInconsistencies } = useGetAllSubsetInconsistencies(
    matterId,
    subsetId,
  );

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Inconsistencies', path: 'inconsistencies' },
  });

  const { mutateAsync: createInconsistencyReport, isLoading: isCreatingReport } =
    useCreateInconsistencyReport(subsetId);

  const [globalFilter, setGlobalFilter] = useState<InconsistencyGlobalFilter>({
    query: '',
    hasRelevantFactsFilter: false,
    includedDocs: new Map<string, MyOptionType>(),
    excludedDocs: new Map<string, MyOptionType>(),
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  const clearAllFilters = () => {
    setGlobalFilter({
      query: '',
      hasRelevantFactsFilter: false,
      includedDocs: new Map(),
      excludedDocs: new Map(),
    });
    setColumnFilters([]);
  };

  // Extract document IDs from an inconsistency's facts
  const getDocIdsFromInconsistency = (inconsistency: Inconsistency): string[] => {
    const docIds = new Set<string>();
    inconsistency.facts.forEach((inconsistencyFact) => {
      inconsistencyFact.fact?.locations?.forEach((location) => {
        if (location.docId) {
          docIds.add(location.docId);
        }
      });
    });
    return Array.from(docIds);
  };

  const customGlobalFilter: FilterFn<Inconsistency> = (row, columnId, filterValue: InconsistencyGlobalFilter) => {
    const { query, hasRelevantFactsFilter, includedDocs, excludedDocs } = filterValue;

    // Document filter check
    if (includedDocs.size > 0 || excludedDocs.size > 0) {
      const docIds = getDocIdsFromInconsistency(row.original);

      // If includedDocs is set, at least one doc must match
      if (includedDocs.size > 0 && !docIds.some((id) => includedDocs.has(id))) {
        return false;
      }

      // If excludedDocs is set, no doc should match
      if (excludedDocs.size > 0 && docIds.some((id) => excludedDocs.has(id))) {
        return false;
      }
    }

    if (hasRelevantFactsFilter && !row.original.facts.some((f) => f.fact.relevance === Relevance.Relevant)) {
      return false;
    }

    if (query) {
      return filterFns.includesString(row, columnId, query, () => null);
    }

    return true;
  };

  const table = useReactTable({
    data: inconsistencies ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    globalFilterFn: customGlobalFilter,
    state: {
      globalFilter,
      rowSelection,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
      sorting: [{ id: 'level', desc: false }],
    },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    enableRowSelection: true,
  });

  // Build global filter pills
  const globalFilterPills = useMemo(() => {
    const pills: FilterPill[] = [];

    if (globalFilter.hasRelevantFactsFilter) {
      pills.push({
        id: 'hasRelevantFacts',
        label: 'Contains relevant facts',
        color: CHIP_COLOURS.hasRelevantFacts,
        onRemove: () => setGlobalFilter((prev) => ({ ...prev, hasRelevantFactsFilter: false })),
      });
    }

    if (globalFilter.includedDocs.size > 0) {
      pills.push({
        id: 'docIds',
        label: `${globalFilter.includedDocs.size} doc${globalFilter.includedDocs.size !== 1 ? 's' : ''}`,
        color: CHIP_COLOURS.default,
        onRemove: () => setGlobalFilter((prev) => ({ ...prev, includedDocs: new Map() })),
      });
    }

    if (globalFilter.excludedDocs.size > 0) {
      pills.push({
        id: 'excludedDocIds',
        label: `${globalFilter.excludedDocs.size} excluded doc${globalFilter.excludedDocs.size !== 1 ? 's' : ''}`,
        color: CHIP_COLOURS.default,
        onRemove: () => setGlobalFilter((prev) => ({ ...prev, excludedDocs: new Map() })),
      });
    }

    return pills;
  }, [globalFilter.hasRelevantFactsFilter, globalFilter.includedDocs, globalFilter.excludedDocs]);

  const filterCount = useMemo(() => {
    let count = 0;

    // Count relevant facts filter
    if (globalFilter.hasRelevantFactsFilter) count++;

    // Count included documents filter
    if (globalFilter.includedDocs.size > 0) count++;

    // Count excluded documents filter
    if (globalFilter.excludedDocs.size > 0) count++;

    // Count column filters
    count += columnFilters.length;

    return count;
  }, [globalFilter, columnFilters]);

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <PageHeader>
            <div className="flex items-center gap-4">
              <PageTitle text="Inconsistencies" />
              <SubsetChip subsetId={subsetId} className="mt-0.5" />
            </div>
          </PageHeader>

          <div className="flex w-full gap-2">
            <SearchInput
              placeholder="Search inconsistencies..."
              className="grow"
              value={globalFilter.query}
              onChange={(e) => setGlobalFilter({ ...globalFilter, query: e.target.value })}
            />

            <Button
              disabled={!selectedCount || isCreatingReport}
              onClick={async () => {
                const report = await createInconsistencyReport(Object.keys(rowSelection));
                trackCustomEvent(TrackingEventsV2.CreateInconsistencyReport, {
                  caseId: subsetId,
                  isAllDocuments: subset?.isAllDocuments,
                });
                navigate(`/matters/${matterId}/subsets/${subsetId}/reports/${report.id}`);
              }}
            >
              <Icon className="lucide--folder" />
              <span>{isCreatingReport ? 'Creating Report...' : 'Create Report'}</span>
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button disabled={!inconsistencies}>
                  <Icon className="lucide--funnel-plus" />
                  <span>Add Filters</span>
                  <CountBadge count={filterCount} />
                </Button>
              </PopoverTrigger>
              {inconsistencies && (
                <FilterPopover
                  inconsistencies={inconsistencies}
                  typeColumn={table.getColumn('type')}
                  levelColumn={table.getColumn('level')}
                  sourceColumn={table.getColumn('source')}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  clearAllFilters={clearAllFilters}
                />
              )}
            </Popover>

            <Button onClick={clearAllFilters}>
              <Icon className="lucide--funnel-x" />
              <span>Clear All Filters</span>
            </Button>
          </div>

          <FilterPills pills={[...globalFilterPills, ...buildClientSideFilterPills(columnFilters, setColumnFilters)]} />

          <div className="flex items-center justify-between pt-4 pb-3">
            <div className="flex items-center gap-3 pl-1 text-xs text-gray-500">
              <Checkbox
                checked={table.getIsAllRowsSelected()}
                onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)}
              />
              <p>
                <strong>{selectedCount}</strong> Inconsistencies selected
              </p>
            </div>

            <TablePagination table={table} plural="inconsistencies" variant="sm" />
          </div>
        </PageContainer>

        {inconsistencies && inconsistencies.length > 0 ? (
          <Accordion type="multiple" className="mx-5 flex flex-col gap-5 overflow-auto">
            {table.getRowModel().rows.map((row) => (
              <InconsistencyCard key={row.id} row={row} inconsistency={row.original} />
            ))}
          </Accordion>
        ) : isLoadingInconsistencies ? (
          <InconsistenciesLoader />
        ) : (
          <Empty className="py-16">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--alert-triangle" />
              </EmptyMedia>
              <EmptyTitle>No inconsistencies found</EmptyTitle>
              <EmptyDescription>No inconsistencies which match these filters.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
    </>
  );
}
